import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Cart, UnifiedShippingChoice} from '../../../model/cart/Cart';
import {environment} from '../../../environments/environment';
import {LanguagePipe} from '../../pipe/language.pipe';
import {StartupService} from '../../service-ui/startup.service';
import {OrganizationService} from '../../service-data/organization.service';

/**
 * Descriptor for one entry in the transit-day filter chip row.
 * Rendered as a toggleable chip built from whatever ranges actually
 * appear in the current `unifiedShippingChoices` â€” never a fixed set.
 */
export interface TransitFilterOption {
  /** Stable key: `"min\u0000max"` or `"unknown"`. Used as filter set membership. */
  key: string;
  /** Pre-formatted display label (e.g. "1â€“3 days" or "No estimate"). */
  label: string;
  /** `min` and `max` are `null` for the unknown-estimate bucket. */
  min: number | null;
  max: number | null;
  /** Sort key so unknown always sinks to the end. */
  sortKey: number;
}

/**
 * Shipping-options section of the checkout accordion.
 *
 * Extracted from `CartPageComponent` so the cart page only orchestrates
 * the overall flow and each section owns its own markup + state helpers.
 * The cart and the readiness flags remain owned by the parent â€” this
 * component only displays and reacts.
 *
 * Carries the same `legacy-controls` host class as `CartPageComponent`
 * so the existing Material button styling is preserved per `AGENTS.md`'s
 * "Modern controls" convention.
 */
@Component({
  selector: 'app-cart-shipping-section',
  templateUrl: './cart-shipping-section.component.html',
  styleUrls: ['../cart-page/checkout-shared.css', './cart-shipping-section.component.css']
})
export class CartShippingSectionComponent {
  @Input() cart: Cart;
  @Input() addressReady: boolean = false;
  @Input() shippingReady: boolean = false;
  @Input() userControlledAccordion: boolean = false;
  @Input() facilityFriendlyNames: { [id: string]: string } = {};

  @Output() userControlledAccordionChange = new EventEmitter<boolean>();
  /**
   * Fires whenever the user picks a shipping choice from the unified
   * list. The parent (`CartPageComponent`) listens to flip
   * `shippingReady` so the payment section becomes eligible to open
   * and the summary bar reflects the new shipping cost.
   */
  @Output() shippingChoicePicked = new EventEmitter<UnifiedShippingChoice>();
  /**
   * Fires when the component auto-seeds a default shipping choice
   * (organization preference or cheapest). Distinct from
   * {@link shippingChoicePicked} — the parent must NOT flip
   * `shippingReady` on this event (that would collapse the accordion
   * out from under the user before they see the picker; see the
   * long-form note in {@link maybeAutoSelectChoice}). The parent
   * DOES need to know so it can recompute downstream figures that
   * depend on the shipping choice (sales tax, order-summary totals)
   * — otherwise the "Organization default" the user sees pre-picked
   * doesn't feed the totals until they manually click a different
   * row and click back, which defeats the purpose of a default
   * (bug 2026-07-20).
   */
  @Output() shippingChoiceAutoSelected = new EventEmitter<UnifiedShippingChoice>();

  /**
   * State of the "Set as organization default" button. Drives the
   * label + spinner + inline confirmation shown next to the shipping
   * picker when the current selection differs from the org's stored
   * `preferredCarrier` / `preferredServiceLevel`.
   *
   *  - `IDLE`   — Button is offered; click to save.
   *  - `SAVING` — PUT in flight; button disabled, spinner visible.
   *  - `SAVED`  — Server accepted; brief green "Saved" confirmation
   *               replaces the button for ~3 seconds before we
   *               reset to `IDLE` (or the button disappears entirely
   *               because the new preference now matches selection).
   *  - `ERROR`  — PUT failed; inline error message + retry available.
   */
  setDefaultState: 'IDLE' | 'SAVING' | 'SAVED' | 'ERROR' = 'IDLE';
  /** Human-readable error surfaced under the button on `ERROR`. */
  setDefaultErrorMessage: string = '';
  /** Handle for the `SAVED → IDLE` reset timer so a new click cancels it. */
  private savedResetHandle: any = null;

  constructor(
      public startupService: StartupService,
      private organizationService: OrganizationService) {}

  /**
   * Signature of the last choice set we auto-selected against, so
   * `unifiedShippingChoices` (a live getter recomputed on every CD
   * tick) doesn't reset the user's manual pick every render.
   * See {@link maybeAutoSelectChoice}.
   */
  private lastAutoSelectSignature: string = '';

  /**
   * True when the current `cart.selected*` shipping selection was
   * seated by {@link maybeAutoSelectChoice} rather than by an
   * explicit user click on a radio. Cleared the moment the user
   * clicks a row (see {@link onChoiceSelected}). Lets auto-select
   * safely REPLACE a previously auto-selected choice (e.g. swap the
   * initially-cheapest pick for the org's preferred (carrier,
   * serviceLevel) once its rate finally lands) while still refusing
   * to stomp a manual user pick.
   */
  private lastAutoSelectWasAuto: boolean = false;

  /**
   * Empty set = no carrier filter applied (all carriers pass).
   * Non-empty = only the listed carriers are shown. Membership is
   * toggled by user clicks on the carrier chip row.
   */
  carrierFilter: Set<string> = new Set<string>();

  /**
   * Empty set = no transit filter. Non-empty set contains one or more
   * `TransitFilterOption.key` values (`"min\u0000max"` or
   * `"unknown"`). Matches choices whose transit range hashes to any
   * key in the set.
   */
  transitFilter: Set<string> = new Set<string>();

  /**
   * `true` = show every choice that passes the carrier + transit
   * filters. `false` (default) = narrow to the per-carrier
   * Pareto-optimal set (see {@link bestChoiceKeys}) — the "Best
   * options" view. Toggled by the header button.
   *
   * Defaulted `false` per 2026-07-09 UX feedback: users prefer the
   * short curated list first and can flip to "Show all options" when
   * they want to see every rate. The `(+N)` hint on the toggle button
   * tells them how many additional rows are hiding behind the filter
   * so an empty-looking picker on a cart with only same-price rates
   * is not misread as "no options."
   */
  showAllOptions: boolean = false;

  /**
   * Deterministic hue palette used by {@link getCarrierColor}. Picked
   * to be visually distinct at typical badge sizes and not clash with
   * the existing section-state colours in `checkout-shared.css`
   * (green ready / orange not-ready / pink default). Values are
   * `background|border|text` triples so callers can style a pill in
   * one lookup.
   */
  private static readonly CARRIER_PALETTE: Array<{ bg: string; border: string; fg: string }> = [
    { bg: '#e3f2fd', border: '#90caf9', fg: '#0d47a1' }, // blue
    { bg: '#fff3e0', border: '#ffcc80', fg: '#e65100' }, // orange
    { bg: '#f3e5f5', border: '#ce93d8', fg: '#4a148c' }, // purple
    { bg: '#e0f2f1', border: '#80cbc4', fg: '#004d40' }, // teal
    { bg: '#fce4ec', border: '#f48fb1', fg: '#880e4f' }, // pink
    { bg: '#f1f8e9', border: '#c5e1a5', fg: '#33691e' }, // light green
    { bg: '#ede7f6', border: '#b39ddb', fg: '#311b92' }, // deep purple
    { bg: '#fff8e1', border: '#ffe082', fg: '#ff6f00' }  // amber
  ];

  /** Material icon name for the section header. */
  shippingSectionIcon(): string {
    if (!this.addressReady) { return 'block'; }
    if (this.cart && this.cart.shippingStatus === 'READY' && this.cart.selectedOrderOption) { return 'check_circle'; }
    if (this.isShippingComputing) { return 'hourglass_empty'; }
    if (this.cart && this.cart.shippingStatus === 'ERROR') { return 'error_outline'; }
    return 'local_shipping';
  }

  /** State-based css class for the section header icon. */
  shippingSectionClass(): string {
    if (!this.addressReady) { return 'section-blocked'; }
    if (this.cart && this.cart.shippingStatus === 'READY' && this.cart.selectedOrderOption) { return 'section-ready'; }
    if (this.isShippingComputing) { return 'section-computing'; }
    if (this.cart && this.cart.shippingStatus === 'ERROR') { return 'section-error'; }
    return 'section-not-ready';
  }

  /**
   * Flattened `(carrier, serviceLevel, cost, backing package layout)`
   * list surfaced by the model. See
   * {@link Cart.getUnifiedShippingChoices} for the algorithm and the
   * motivation for collapsing the raw OrderOption Ã— ShippingOption
   * tree â€” the user cares about "which carrier + service, at what
   * price", not "which backing box layout".
   *
   * Recomputed on every change-detection tick; the underlying tree
   * updates asynchronously as per-package `getShippingOptions`
   * responses land, so the accessor stays a live view rather than a
   * cached snapshot. Cost is negligible (typical carts have <10
   * unique `(carrier, service)` pairs across a couple of layouts).
   *
   * Side-effect: after the raw list is built, we invoke
   * {@link maybeAutoSelectChoice} to seed the radio group with the
   * organization's preferred carrier + service (or, if none, the
   * cheapest option). Doing it here keeps the auto-select logic
   * outside of Angular lifecycle hooks (`ngOnChanges` etc.) so it
   * naturally reacts to asynchronous rate arrivals without extra
   * subscriptions.
   */
  get unifiedShippingChoices(): UnifiedShippingChoice[] {
    if (!this.cart) { return []; }
    const choices = this.cart.getUnifiedShippingChoices();
    if (!environment.production && choices.length === 0 && this.cart.orderOptions && this.cart.orderOptions.length > 0) {
      // Diagnostic â€” log the raw tree state so it's obvious in the
      // browser console why the picker looks empty. Only fires when
      // there's SOMETHING to compute from but nothing came out.
      try {
        const summary = this.cart.orderOptions.map(oo => ({
          layoutCarrier: oo.carrier,
          layoutServiceLevel: oo.serviceLevel,
          pnoos: (oo.printNodeOrderOptions || []).map(pnoo => ({
            facilityId: pnoo.facilityId,
            computationStatus: pnoo.computationStatus,
            packageCount: (pnoo.shippingPackages || []).length,
            packages: (pnoo.shippingPackages || []).map(pkg => ({
              qty: pkg.quantity,
              hxwxd: `${pkg.height}x${pkg.width}x${pkg.depth}`,
              shippingOptions: (pkg.shippingOptions || []).map(o => `${o.carrier}/${o.serviceLevel}=$${o.cost}`)
            }))
          }))
        }));
        // eslint-disable-next-line no-console
        console.log('[cart-shipping] no unified choices yet â€” raw tree:', summary);
      } catch (e) {
        console.error('[cart-shipping] diagnostic serialisation failed', e);
      }
    }
    this.maybeAutoSelectChoice(choices);
    return choices;
  }

  /**
   * Distinct carrier names present in the current `unifiedShippingChoices`,
   * sorted alphabetically. Drives the carrier filter chip row â€” never
   * a hardcoded list; the chips appear/disappear as the server's rate
   * responses land. Preserves the first-seen palette index across
   * renders (see {@link getCarrierColor}).
   */
  get availableCarriers(): string[] {
    const seen = new Set<string>();
    for (const c of this.unifiedShippingChoices) {
      if (c.carrier) { seen.add(c.carrier); }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }

  /**
   * Distinct transit-day ranges present in the current
   * `unifiedShippingChoices`, sorted fastest-first. Drives the
   * transit-days filter chip row. The unknown-estimate bucket (rate
   * rows without a range) is appended last as a single "No estimate"
   * chip.
   */
  get availableTransitBuckets(): TransitFilterOption[] {
    const seen = new Map<string, TransitFilterOption>();
    for (const c of this.unifiedShippingChoices) {
      const key = this.transitKeyOf(c);
      if (seen.has(key)) { continue; }
      if (key === 'unknown') {
        seen.set(key, {
          key,
          label: LanguagePipe.get('No estimate'),
          min: null,
          max: null,
          sortKey: Number.MAX_SAFE_INTEGER
        });
      } else {
        const min = c.deliveryDaysMin as number;
        const max = c.deliveryDaysMax as number;
        seen.set(key, {
          key,
          label: this.formatTransitChipLabel(min, max),
          min, max,
          // Sort by max first (fastest overall arrival wins), then by
          // min as a tie-break. Pack both into one integer for a
          // single-comparison sort.
          sortKey: max * 100 + min
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.sortKey - b.sortKey);
  }

  /**
   * `unifiedShippingChoices` narrowed by the carrier + transit
   * filters (order preserved). Used by both the "best-only" and
   * "show-all" views; the further best-only narrowing happens in
   * {@link visibleShippingChoices}.
   */
  get filteredShippingChoices(): UnifiedShippingChoice[] {
    const flat = this.unifiedShippingChoices;
    if (!flat || flat.length === 0) { return []; }
    const carrierActive = this.carrierFilter.size > 0;
    const transitActive = this.transitFilter.size > 0;
    if (!carrierActive && !transitActive) { return flat; }
    return flat.filter(c => {
      if (carrierActive && !this.carrierFilter.has(c.carrier)) { return false; }
      if (transitActive && !this.transitFilter.has(this.transitKeyOf(c))) { return false; }
      return true;
    });
  }

  /**
   * The final list of rows the picker renders. Applies:
   *  1. `filteredShippingChoices` (carrier + transit filters), then
   *  2. When `showAllOptions === false`, further narrows to the
   *     per-carrier Pareto-optimal choices via {@link bestChoiceKeys}.
   *
   * The currently-selected choice always passes through, even if it
   * would otherwise be filtered out â€” otherwise the user would lose
   * their pick the moment they flip a filter that excludes it, which
   * feels broken. The selected row is annotated with a subtle "kept
   * visible because it's your current pick" hint via CSS (see the
   * `unified-shipping-choice-selected` state style).
   */
  get visibleShippingChoices(): UnifiedShippingChoice[] {
    // `unifiedShippingChoices` returns fresh objects on every getter
    // access (the model's `getUnifiedShippingChoices()` rebuilds the
    // list from the current OrderOption tree each call). We MUST call
    // it once and reuse the reference throughout this method — using
    // `this.filteredShippingChoices` (which internally calls the
    // getter again) and then `this.unifiedShippingChoices` (yet
    // another call) means `visible` and `flat` contain objects that
    // are logically equal but never `===`, which broke the
    // "rescue the currently-selected row" step and duplicated the
    // selected row when it was NOT filtered out (bug 2026-07-09).
    const flat = this.unifiedShippingChoices;
    if (!flat || flat.length === 0) { return []; }
    const carrierActive = this.carrierFilter.size > 0;
    const transitActive = this.transitFilter.size > 0;
    const filtered = (!carrierActive && !transitActive)
      ? flat
      : flat.filter(c => {
          if (carrierActive && !this.carrierFilter.has(c.carrier)) { return false; }
          if (transitActive && !this.transitFilter.has(this.transitKeyOf(c))) { return false; }
          return true;
        });
    let visible: UnifiedShippingChoice[];
    if (this.showAllOptions) {
      visible = filtered;
    } else {
      const bestKeys = this.bestChoiceKeysFrom(flat);
      visible = filtered.filter(c => bestKeys.has(this.choiceKeyOf(c)));
    }
    // Rescue the currently-selected choice if the filters/best-only
    // narrowing would hide it. Compare by stable
    // `(carrier, serviceLevel)` key — object identity fails because
    // `flat` and `visible` come from separate getter calls above and
    // callers refer to fresh objects. Insert in the original
    // cost-sort position so the surrounding rows stay in order.
    const selected = flat.find(c => this.isChoiceSelected(c));
    if (selected) {
      const selectedKey = this.choiceKeyOf(selected);
      const alreadyVisible = visible.some(v => this.choiceKeyOf(v) === selectedKey);
      if (!alreadyVisible) {
        const idx = flat.indexOf(selected);
        // Re-project into `visible` order: find the first `visible` row
        // whose flat-index is greater than the selected's flat-index.
        const insertAt = visible.findIndex(v => flat.indexOf(v) > idx);
        if (insertAt === -1) {
          visible = [...visible, selected];
        } else {
          visible = [...visible.slice(0, insertAt), selected, ...visible.slice(insertAt)];
        }
      }
    }
    // Dev-only diagnostic: unexpected empty picker despite having
    // both underlying choices and passing filters. Points a finger
    // at whichever narrowing step ate everything. Never fires in
    // production builds.
    if (!environment.production && flat.length > 0 && visible.length === 0) {
      try {
        const bestKeys = this.showAllOptions ? null : Array.from(this.bestChoiceKeysFrom(flat));
        console.warn('[cart-shipping] visibleShippingChoices unexpectedly empty', {
          unifiedCount: flat.length,
          filteredCount: filtered.length,
          showAllOptions: this.showAllOptions,
          carrierFilter: Array.from(this.carrierFilter),
          transitFilter: Array.from(this.transitFilter),
          bestKeys,
          flat: flat.map(c => ({
            key: this.choiceKeyOf(c),
            carrier: c.carrier,
            serviceLevel: c.serviceLevel,
            totalCost: c.totalCost,
            dMin: c.deliveryDaysMin,
            dMax: c.deliveryDaysMax
          }))
        });
      } catch (e) {
        console.error('[cart-shipping] visibleShippingChoices diagnostic failed', e);
      }
    }
    return visible;
  }

  /**
   * Set of `choiceKeyOf(...)` strings marking the per-carrier
   * Pareto-optimal choices â€” an option is "best" when no other option
   * of the same carrier beats it on BOTH cost and `deliveryDaysMax`
   * (at least one strict inequality). For carriers whose rows all
   * lack a transit estimate, the fallback is just the cheapest row.
   * A carrier with only one row always has that row in the best set.
   *
   * Independent of the current filters â€” we compute "best" over the
   * full `unifiedShippingChoices`, then the render pipeline intersects
   * with `filteredShippingChoices`. That way flipping a filter never
   * changes what counts as "best" (only what's shown), which matches
   * how the user thinks about it: "these are best regardless of what
   * I'm filtering on right now".
   */
  get bestChoiceKeys(): Set<string> {
    return this.bestChoiceKeysFrom(this.unifiedShippingChoices);
  }

  /**
   * Same as {@link bestChoiceKeys} but computed against an explicit
   * choice list — used by {@link visibleShippingChoices} so it can
   * reuse the flat list it already fetched instead of triggering
   * another `getUnifiedShippingChoices()` call (which would return
   * fresh objects and break identity comparisons downstream).
   */
  private bestChoiceKeysFrom(flat: UnifiedShippingChoice[]): Set<string> {
    const out = new Set<string>();
    if (!flat || flat.length === 0) { return out; }
    // Group by carrier.
    const byCarrier = new Map<string, UnifiedShippingChoice[]>();
    for (const c of flat) {
      const arr = byCarrier.get(c.carrier) || [];
      arr.push(c);
      byCarrier.set(c.carrier, arr);
    }
    for (const rows of Array.from(byCarrier.values())) {
      // Only one option â†’ it's the best by default.
      if (rows.length === 1) {
        out.add(this.choiceKeyOf(rows[0]));
        continue;
      }
      // Any rows without a transit estimate collapse the Pareto
      // criterion to cost alone (max transit is unknown, so we can't
      // dominate on it). Keep the single cheapest of the missing-
      // transit rows so the carrier still shows something.
      const withRange = rows.filter(r => this.hasDeliveryEstimate(r));
      const withoutRange = rows.filter(r => !this.hasDeliveryEstimate(r));
      if (withRange.length === 0) {
        // No transit info at all for this carrier â€” cheapest wins.
        const cheapest = rows.slice().sort((a, b) => a.totalCost - b.totalCost)[0];
        out.add(this.choiceKeyOf(cheapest));
        continue;
      }
      // Pareto frontier on (cost asc, transit-max asc).
      for (const candidate of withRange) {
        let dominated = false;
        for (const other of withRange) {
          if (other === candidate) { continue; }
          const otherMax = other.deliveryDaysMax as number;
          const candMax = candidate.deliveryDaysMax as number;
          const costBetterOrEqual = other.totalCost <= candidate.totalCost;
          const transitBetterOrEqual = otherMax <= candMax;
          const anyStrict = other.totalCost < candidate.totalCost || otherMax < candMax;
          if (costBetterOrEqual && transitBetterOrEqual && anyStrict) {
            dominated = true;
            break;
          }
        }
        if (!dominated) {
          out.add(this.choiceKeyOf(candidate));
        }
      }
      // Also include the cheapest of the missing-transit rows if it
      // strictly beats every ranged row on cost â€” otherwise it's
      // hard to describe as "best" (we don't know how slow it is).
      if (withoutRange.length > 0) {
        const cheapestNoRange = withoutRange.slice().sort((a, b) => a.totalCost - b.totalCost)[0];
        const minRangedCost = withRange.reduce((m, r) => Math.min(m, r.totalCost), Number.POSITIVE_INFINITY);
        if (cheapestNoRange.totalCost < minRangedCost) {
          out.add(this.choiceKeyOf(cheapestNoRange));
        }
      }
    }
    return out;
  }

  /**
   * Count of choices that pass the filters but are hidden by the
   * best-only view. Drives the "Show N more" hint on the toggle
   * button so the user can gauge whether flipping to "Show all" will
   * meaningfully expand the list.
   */
  get hiddenByBestOnlyCount(): number {
    if (this.showAllOptions) { return 0; }
    return this.filteredShippingChoices.length - this.visibleShippingChoices.length;
  }

  /**
   * Seed the shipping radio group with a sensible default once rate
   * choices are available:
   *
   * 1. If the organization has both a `preferredCarrier` and a
   *    `preferredServiceLevel` AND the current choice list contains a
   *    matching row, pick that.
   * 2. Otherwise pick the first (cheapest) row — the model already
   *    sorts choices by ascending `totalCost` (see
   *    `Cart.getUnifiedShippingChoices` step 4).
   *
   * Skips when the user has already picked something manually — we
   * compare the current selection against the choices and only act
   * when nothing is selected. Also skips when the exact set of
   * choices hasn't changed since the last auto-select attempt, so we
   * don't keep resetting the user's pick every change-detection tick.
   *
   * IMPORTANT — does NOT emit `shippingChoicePicked` (2026-07-09 fix).
   * Emitting on auto-select caused the parent to flip `shippingReady`
   * to true the instant the first per-package rate landed, which in
   * turn made the accordion's
   * `[expanded]="!userControlledAccordion && addressReady && !shippingReady"`
   * binding collapse the panel out from under the user before they
   * ever saw the list of choices. The user report was "no shipping
   * options are displayed" even though rate data had clearly landed
   * (filter chips had populated). Auto-select still writes the
   * default choice into the cart so `shippingCost` and the summary
   * bar populate; only the "user is finished with shipping" signal
   * is withheld until the user actively clicks a radio (which fires
   * `onChoiceSelected` and legitimately emits the event).
   */
  private maybeAutoSelectChoice(choices: UnifiedShippingChoice[]): void {
    if (!this.cart || !choices || choices.length === 0) { return; }
    // Signature captures the ordered (carrier,serviceLevel,totalCost)
    // tuples so we re-run auto-select only when the choice set really
    // changed (new rates arrived, a quantity edit invalidated the
    // previous set, etc.) — NOT on every render.
    const signature = choices.map(c => `${c.carrier}|${c.serviceLevel}|${c.totalCost.toFixed(2)}`).join('\u0001');
    if (signature === this.lastAutoSelectSignature) { return; }
    // If the user (or a previous auto-select pass) already picked one
    // of the CURRENT choices, respect that pick — BUT still refresh
    // the cart's `shippingCost` / `packingAndPackagingCost` and
    // re-emit so the parent recomputes sales tax against any updated
    // numbers. Rate data arrives asynchronously per package: the
    // first `getUnifiedShippingChoices()` pass may seat a default
    // whose cost reflects only the packages that had landed by then,
    // and subsequent passes will produce a different `totalCost` for
    // the SAME (carrier, serviceLevel) as more per-package rates
    // come in. Without this refresh, the cart holds the stale early
    // costs and sales tax is computed against a wrong shipping total
    // (bug 2026-07-20 pass 2).
    const existingSelection = choices.find(c => this.isChoiceSelected(c));
    // If the current selection was seated by a PRIOR auto-select
    // pass AND the org's preferred (carrier, serviceLevel) now
    // appears in the choices list, upgrade: the placeholder
    // auto-pick should yield to the just-arrived preferred rate
    // rather than block itself in via the "existingSelection"
    // branch (bug 2026-07-20 pass 3).
    const org = this.cart.organization;
    const preferredNowAvailable = (org && org.preferredCarrier && org.preferredServiceLevel)
      ? choices.find(c => c.carrier === org.preferredCarrier && c.serviceLevel === org.preferredServiceLevel)
      : undefined;
    const shouldUpgradeToPreferred = !!preferredNowAvailable
      && this.lastAutoSelectWasAuto
      && existingSelection !== preferredNowAvailable;
    if (existingSelection && !shouldUpgradeToPreferred) {
      this.lastAutoSelectSignature = signature;
      Promise.resolve().then(() => {
        if (!this.cart) { return; }
        const shippingChanged = this.cart.shippingCost !== existingSelection.shippingCost;
        const packingChanged = this.cart.packingAndPackagingCost !== existingSelection.packingCost;
        if (shippingChanged || packingChanged) {
          this.cart.setSelectedShippingChoice(existingSelection);
          this.shippingChoiceAutoSelected.emit(existingSelection);
        }
      });
      return;
    }
    // Prefer the org's preferred (carrier, serviceLevel) if it's on offer.
    let picked: UnifiedShippingChoice | undefined = preferredNowAvailable;
    // Fall back to the cheapest option (choices are already sorted).
    if (!picked) { picked = choices[0]; }
    this.lastAutoSelectSignature = signature;
    // Defer the cart mutation until after the current CD pass so we
    // don't mutate cart state mid-render (`ExpressionChanged...` guard).
    // Deliberately NOT emitting `shippingChoicePicked` here — see
    // the JSDoc above for why (accordion auto-collapse regression).
    Promise.resolve().then(() => {
      // Re-check preconditions in case something moved during the tick.
      if (!this.cart || !picked) { return; }
      // If a MANUAL user pick landed during the microtask, don't
      // stomp it. A prior AUTO pick is fair game to replace — that
      // is exactly the case that surfaces when the org's preferred
      // (carrier, serviceLevel) rate arrives after we've already
      // seated the cheapest option as a placeholder default: we
      // must switch to the preferred pick and refresh the cart
      // costs, otherwise the cart shows the org default as selected
      // but its `shippingCost` / `packingAndPackagingCost` still
      // reflect the placeholder (bug 2026-07-20 pass 3).
      const stillOffered = this.cart.getUnifiedShippingChoices().some(c => this.isChoiceSelected(c));
      if (stillOffered && !this.lastAutoSelectWasAuto) { return; }
      this.cart.setSelectedShippingChoice(picked);
      this.lastAutoSelectWasAuto = true;
      // Notify the parent via the dedicated auto-select event so it
      // can recompute sales tax and refresh the order-summary totals
      // against the newly-seeded shipping cost — WITHOUT flipping
      // `shippingReady` (which would auto-collapse the accordion
      // before the user has seen the picker; see the JSDoc above).
      this.shippingChoiceAutoSelected.emit(picked);
    });
  }

  /**
   * True when this choice matches the organization's preferred
   * `(carrier, serviceLevel)` pair. Drives the "Recommended" badge in
   * the row. `false` when the org has no preference set.
   */
  isPreferredChoice(choice: UnifiedShippingChoice): boolean {
    if (!this.cart || !this.cart.organization || !choice) { return false; }
    const org = this.cart.organization;
    return !!org.preferredCarrier
        && !!org.preferredServiceLevel
        && choice.carrier === org.preferredCarrier
        && choice.serviceLevel === org.preferredServiceLevel;
  }

  /**
   * True for the globally-cheapest row IF there are at least two
   * distinct-cost choices. Suppresses the "Best price" badge when
   * everything ties for cheapest (e.g. a single option, or two
   * carriers priced identically) since the badge conveys no
   * information in those cases.
   */
  isBestPriceChoice(choice: UnifiedShippingChoice): boolean {
    const flat = this.unifiedShippingChoices;
    if (!flat || flat.length < 2) { return false; }
    if (flat[0] !== choice) { return false; }
    return flat[0].totalCost < flat[1].totalCost;
  }

  /**
   * True when this choice is in the Pareto-optimal set for its
   * carrier (see {@link bestChoiceKeys}). Drives the green
   * check-mark icon in the row.
   */
  isBestForCarrier(choice: UnifiedShippingChoice): boolean {
    if (!choice) { return false; }
    return this.bestChoiceKeys.has(this.choiceKeyOf(choice));
  }

  /**
   * True when this choice has a usable transit-day estimate to
   * display. `false` when the server did not populate the range
   * fields for one or more of the underlying rate rows (see the
   * `anyRangeMissing` short-circuit in
   * `Cart.getUnifiedShippingChoices`); the picker suppresses the
   * transit badge entirely in that case rather than showing a
   * misleading partial estimate.
   */
  hasDeliveryEstimate(choice: UnifiedShippingChoice): boolean {
    if (!choice) { return false; }
    return choice.deliveryDaysMin != null && choice.deliveryDaysMax != null;
  }

  /**
   * Format the transit-day range for display. Collapses a same-day
   * range to a single value ("2" rather than "2â€“2") and returns the
   * bare number so the template can add the "business day(s)"
   * i18n label around it. Returns an empty string when
   * {@link hasDeliveryEstimate} is false; callers guard on that.
   */
  formatDeliveryDaysRange(choice: UnifiedShippingChoice): string {
    if (!this.hasDeliveryEstimate(choice)) { return ''; }
    const min = choice.deliveryDaysMin;
    const max = choice.deliveryDaysMax;
    if (min === max) { return `${min}`; }
    // Uses an en-dash to match the CSS-generated separator style
    // elsewhere in the picker.
    return `${min}\u2013${max}`;
  }

  /**
   * True while the cart is waiting on any part of the shipping-rate
   * pipeline. Distinguishes the two "no rows yet" scenarios so the
   * template can show either a spinner (still computing) or a "no
   * options" placeholder (finished but nothing came back).
   */
  get isShippingComputing(): boolean {
    if (!this.cart) { return false; }
    if (this.cart.shippingStatus === 'COMPUTING' || this.cart.shippingStatus === 'PENDING') { return true; }
    // shippingStatus flips to READY very early (right after the first
    // `getPackages` returns). Any per-package `getShippingOptions`
    // still in flight means we're still computing from the user's POV.
    for (const oo of (this.cart.orderOptions || [])) {
      for (const pnoo of (oo.printNodeOrderOptions || [])) {
        if (!pnoo.shippingPackages || pnoo.shippingPackages.length === 0) { return true; }
        for (const pkg of pnoo.shippingPackages) {
          if (pkg.computationStatus === 'COMPUTING' || pkg.computationStatus === 'DIRTY' || pkg.computationStatus === 'INITIAL') { return true; }
        }
      }
    }
    return false;
  }

  /**
   * True when this row is the currently-selected choice â€” matched on
   * `(carrier, serviceLevel, backing OrderOption)` so the radio stays
   * lit through re-renders.
   */
  isChoiceSelected(choice: UnifiedShippingChoice): boolean {
    if (!this.cart || !choice) { return false; }
    return this.cart.selectedOrderOption === choice.orderOption
        && this.cart.selectedCarrier === choice.carrier
        && this.cart.selectedServiceLevel === choice.serviceLevel;
  }

  /**
   * Track function for the shipping-choice `*ngFor`. Prevents
   * mat-radio flicker by keying on the stable `(carrier,serviceLevel)`
   * tuple rather than the choice object's identity â€” the object is
   * rebuilt on every change-detection tick by the accessor above.
   *
   * IMPORTANT — must NOT reference `this`. Angular invokes track-by
   * functions detached from their component context (they run as
   * plain callbacks inside `NgForOf._applyChanges`), so any
   * `this.<method>` call throws `Cannot read properties of
   * undefined` and the exception cancels the differ, silently
   * collapsing the `*ngFor` to zero rendered rows (bug 2026-07-09
   * pass 2: adding `trackBy: trackChoice` made every shipping row
   * vanish). Keep the key derivation inline — the shape has to stay
   * in sync with `choiceKeyOf(...)` in this file.
   */
  trackChoice(_index: number, choice: UnifiedShippingChoice): string {
    return choice ? `${choice.carrier}\u0000${choice.serviceLevel}` : `${_index}`;
  }

  /** Track function for the carrier filter chip row. Keyed on carrier name. */
  trackCarrier(_index: number, carrier: string): string {
    return carrier || `${_index}`;
  }

  /** Track function for the transit-days filter chip row. Keyed on the bucket key. */
  trackTransit(_index: number, option: TransitFilterOption): string {
    return option ? option.key : `${_index}`;
  }

  onChoiceSelected(choice: UnifiedShippingChoice): void {
    if (!this.cart || !choice) { return; }
    this.cart.setSelectedShippingChoice(choice);
    // The current selection is now a MANUAL user pick — clear the
    // auto-select flag so subsequent auto-select passes (when new
    // rates arrive or the org preference changes) do NOT stomp it.
    this.lastAutoSelectWasAuto = false;
    this.shippingChoicePicked.emit(choice);
    // A fresh pick invalidates any "Saved" chip that might still be
    // showing from a previous save — the button label logic below
    // will re-evaluate against the new (carrier, serviceLevel).
    if (this.setDefaultState === 'SAVED') {
      this.clearSavedTimer();
      this.setDefaultState = 'IDLE';
    }
    if (this.setDefaultState === 'ERROR') {
      // Retry cleanly against the new selection.
      this.setDefaultState = 'IDLE';
      this.setDefaultErrorMessage = '';
    }
  }

  // ─── "Set as organization default" button ──────────────────────
  //
  // Offered under the shipping picker when the user has an active
  // shipping selection AND is currently acting in the `organization`
  // role AND that selection differs from the org's stored
  // `preferredCarrier` / `preferredServiceLevel`. Click PUTs the
  // updated organization record and updates `cart.organization` in
  // place so the picker's "Recommended" badge follows the new
  // preference immediately (and the auto-select signature
  // recomputes on the next tick so future re-entries land on the
  // just-saved default).

  /**
   * True when the "Set as organization default" affordance should
   * appear in the picker. Requires:
   *  1. A selection to save (`cart.selectedOrderOption` populated
   *     and both `selectedCarrier` + `selectedServiceLevel` non-empty).
   *  2. Org-admin permission — same gate as the organization
   *     settings page uses (`startupService.selectedRole ===
   *     'organization'`), i.e. the user is currently acting in the
   *     organization role rather than a facility/network role.
   *  3. `cart.organization` present.
   *  4. The current selection actually DIFFERS from the org's
   *     current preference — no point offering the button when it
   *     would be a no-op.
   */
  get canSetOrgPreferredShipping(): boolean {
    if (!this.cart || !this.cart.organization) { return false; }
    if (!this.cart.selectedOrderOption) { return false; }
    if (!this.cart.selectedCarrier || !this.cart.selectedServiceLevel) { return false; }
    if (this.startupService.selectedRole !== 'organization') { return false; }
    const org = this.cart.organization;
    const currentCarrier = (org.preferredCarrier || '').trim();
    const currentService = (org.preferredServiceLevel || '').trim();
    // Trim on both sides so a whitespace-only difference doesn't
    // spuriously offer the save button.
    return currentCarrier !== this.cart.selectedCarrier.trim()
        || currentService !== this.cart.selectedServiceLevel.trim();
  }

  /**
   * Fire the PUT that updates the organization's preferred carrier
   * + service level to match the currently-selected shipping choice.
   * Idempotent-guarded: no-op if a save is already in flight or if
   * the button should not be offered (defence in depth against a
   * stale click after the picker state changed).
   *
   * On success, mutates `cart.organization` in place so the picker's
   * "Recommended" badge and future auto-select seeds pick up the
   * new preference without waiting for an `OrganizationService`
   * cache reload. Also calls `organizationService.reset()` so the
   * settings page (if the user navigates there next) fetches the
   * fresh record rather than serving the stale cached copy.
   */
  setOrgPreferredShipping(): void {
    if (!this.canSetOrgPreferredShipping) { return; }
    if (this.setDefaultState === 'SAVING') { return; }
    const org = this.cart.organization;
    const originalCarrier = org.preferredCarrier;
    const originalService = org.preferredServiceLevel;
    // Optimistic update — apply locally BEFORE the PUT so the
    // picker's "Recommended" badge repaints immediately (the whole
    // point of the button is that the current selection should
    // become the default). Reverted below on error.
    org.preferredCarrier = this.cart.selectedCarrier;
    org.preferredServiceLevel = this.cart.selectedServiceLevel;
    // Nudge the auto-select signature so the next getter call
    // reconsiders — otherwise `lastAutoSelectSignature` still
    // holds the pre-save signature and the picker wouldn't
    // re-seat the badges even though the org preference changed.
    // The signature is derived from the CHOICE list (which hasn't
    // changed), so we clear it to force a rerun.
    this.lastAutoSelectSignature = '';
    this.setDefaultState = 'SAVING';
    this.setDefaultErrorMessage = '';
    this.clearSavedTimer();
    this.organizationService.updateOrganization(org).subscribe({
      next: (updated) => {
        // Server may echo the full org back — fold any changed
        // fields onto the in-place object. We only care that our
        // two preference fields stuck; other fields (updatedAt,
        // etc.) can update opportunistically.
        if (updated) {
          if ((updated as any).preferredCarrier !== undefined) {
            org.preferredCarrier = (updated as any).preferredCarrier;
          }
          if ((updated as any).preferredServiceLevel !== undefined) {
            org.preferredServiceLevel = (updated as any).preferredServiceLevel;
          }
        }
        // Invalidate the org service cache so the settings page
        // fetches fresh next time (matches the pattern used by
        // `OrganizationComponent.save()`).
        try { this.organizationService.reset(); } catch (e) { /* non-fatal */ }
        this.setDefaultState = 'SAVED';
        // Reset to IDLE after a beat. The button visibility gate
        // (`canSetOrgPreferredShipping`) will then hide it entirely
        // because the current selection now matches the freshly-
        // saved preference, so the user just sees the picker
        // return to its normal state.
        this.savedResetHandle = setTimeout(() => {
          this.setDefaultState = 'IDLE';
          this.savedResetHandle = null;
        }, 3000);
      },
      error: (err) => {
        console.error('[cart-shipping] setOrgPreferredShipping failed', err);
        // Revert the optimistic update so the picker doesn't
        // silently claim a preference the server didn't accept.
        org.preferredCarrier = originalCarrier;
        org.preferredServiceLevel = originalService;
        this.setDefaultState = 'ERROR';
        this.setDefaultErrorMessage = LanguagePipe.get('Could not save preference');
      }
    });
  }

  /**
   * Cancel any pending `SAVED → IDLE` reset. Called before
   * scheduling a new save so a rapid double-click doesn't leave
   * an orphan timer that races us back to `IDLE` mid-save.
   */
  private clearSavedTimer(): void {
    if (this.savedResetHandle) {
      clearTimeout(this.savedResetHandle);
      this.savedResetHandle = null;
    }
  }

  /**
   * Public accessor for the stable `(carrier, serviceLevel)` key of a
   * choice. Used by the template as the `[value]` of each
   * `<mat-radio-button>` (which in turn matches
   * `<mat-radio-group [value]="selectedChoiceKey">` — see below).
   *
   * IMPORTANT — we bind the group's `[value]` to a STRING KEY rather
   * than the choice object itself. Reason: `unifiedShippingChoices`
   * returns freshly-constructed objects on every getter access (the
   * model rebuilds the list from the underlying `OrderOption` tree
   * each call), so object-identity comparisons inside
   * `mat-radio-group` (which compares its own `.value` against each
   * button's `.value`) will never match after a re-render, leaving
   * the group's internal `_selected` reference stale. When the user
   * then clicks a different row, `MatRadioButton._onInputInteraction`
   * runs, but the group's state ping-pongs against Angular's
   * `[checked]` binding on the previous row and the click appears to
   * "not stick" (bug 2026-07-09 pass 2: "there's an option selected
   * by default but I can not select any other option"). Using the
   * stable string key sidesteps the identity trap entirely.
   */
  choiceKeyOfPublic(choice: UnifiedShippingChoice): string {
    return this.choiceKeyOf(choice);
  }

  /**
   * Stable key of the currently-selected choice — used by
   * `<mat-radio-group [value]>` to sync the group's internal
   * `_selected` reference with whatever the cart currently says is
   * picked. See {@link choiceKeyOfPublic} for why this is a string
   * and not the choice object. Returns `null` when nothing is
   * selected so the group shows no active row.
   */
  get selectedChoiceKey(): string | null {
    if (!this.cart || !this.cart.selectedOrderOption) { return null; }
    return `${this.cart.selectedCarrier}\u0000${this.cart.selectedServiceLevel}`;
  }

  onAccordionToggle() {
    this.userControlledAccordion = true;
    this.userControlledAccordionChange.emit(true);
  }

  /**
   * Toggle a carrier in the {@link carrierFilter} set. Also
   * refreshes the auto-select signature so the next tick has a
   * chance to reseat the selection if the current pick just got
   * filtered out (though we deliberately keep the current pick
   * visible in the render pipeline â€” see
   * {@link visibleShippingChoices}).
   */
  toggleCarrierFilter(carrier: string): void {
    if (this.carrierFilter.has(carrier)) {
      this.carrierFilter.delete(carrier);
    } else {
      this.carrierFilter.add(carrier);
    }
  }

  /** True when the carrier's chip is currently pressed / active. */
  isCarrierFilterActive(carrier: string): boolean {
    return this.carrierFilter.has(carrier);
  }

  /** Clear every carrier filter (equivalent to "show all carriers"). */
  clearCarrierFilter(): void {
    this.carrierFilter.clear();
  }

  /** Toggle a transit bucket in the {@link transitFilter} set. */
  toggleTransitFilter(option: TransitFilterOption): void {
    if (!option) { return; }
    if (this.transitFilter.has(option.key)) {
      this.transitFilter.delete(option.key);
    } else {
      this.transitFilter.add(option.key);
    }
  }

  /** True when the transit bucket's chip is currently pressed / active. */
  isTransitFilterActive(option: TransitFilterOption): boolean {
    return option ? this.transitFilter.has(option.key) : false;
  }

  /** Clear every transit filter (equivalent to "any transit time"). */
  clearTransitFilter(): void {
    this.transitFilter.clear();
  }

  /** Flip between "best only" and "show every option that passes the filters". */
  toggleShowAll(): void {
    this.showAllOptions = !this.showAllOptions;
  }

  /**
   * Deterministic colour lookup for a carrier â€” same input always
   * returns the same palette triple across renders. Uses a stable
   * ordinal (position in {@link availableCarriers}) rather than a
   * hash so the first carrier alphabetically consistently gets the
   * blue treatment, which reads more predictably than a hash.
   * Wraps modulo the palette length so more carriers than the
   * palette still get sensible colours.
   */
  getCarrierColor(carrier: string): { bg: string; border: string; fg: string } {
    const idx = this.availableCarriers.indexOf(carrier);
    const safeIdx = idx >= 0 ? idx : 0;
    return CartShippingSectionComponent.CARRIER_PALETTE[
      safeIdx % CartShippingSectionComponent.CARRIER_PALETTE.length
    ];
  }

  /**
   * Traffic-light colouring for the transit range: shorter transit
   * times get green, longer trend toward gray. Uses `deliveryDaysMax`
   * (the pessimistic end of the range) as the sort key so a "1â€“7
   * days" option doesn't get the same green as a "1â€“2 days" option.
   * Returns the same palette-triple shape as {@link getCarrierColor}
   * for template symmetry.
   */
  getTransitColor(choice: UnifiedShippingChoice): { bg: string; border: string; fg: string } {
    if (!this.hasDeliveryEstimate(choice)) {
      return { bg: '#eeeeee', border: '#bdbdbd', fg: '#616161' };
    }
    const max = choice.deliveryDaysMax as number;
    if (max <= 1) { return { bg: '#e8f5e9', border: '#a5d6a7', fg: '#2e7d32' }; } // vivid green
    if (max <= 2) { return { bg: '#f1f8e9', border: '#c5e1a5', fg: '#558b2f' }; } // light green
    if (max <= 3) { return { bg: '#fffde7', border: '#fff59d', fg: '#f57f17' }; } // yellow
    if (max <= 5) { return { bg: '#fff3e0', border: '#ffcc80', fg: '#ef6c00' }; } // orange
    if (max <= 7) { return { bg: '#fbe9e7', border: '#ffab91', fg: '#d84315' }; } // deep orange
    return { bg: '#efebe9', border: '#a1887f', fg: '#4e342e' };                    // brown-gray for long
  }

  /**
   * Stable key for a choice used by best-choice membership and
   * `*ngFor` tracking. Same shape used everywhere so callers can
   * intersect sets without ceremony.
   */
  private choiceKeyOf(choice: UnifiedShippingChoice): string {
    return `${choice.carrier}\u0000${choice.serviceLevel}`;
  }

  /** Stable key for the transit bucket a choice falls into. */
  private transitKeyOf(choice: UnifiedShippingChoice): string {
    if (!this.hasDeliveryEstimate(choice)) { return 'unknown'; }
    return `${choice.deliveryDaysMin}\u0000${choice.deliveryDaysMax}`;
  }

  /**
   * Short form used on transit filter chips ("1â€“3 days"). Distinct
   * from `formatDeliveryDaysRange` which returns a bare number for
   * per-row concatenation with "business day(s)". Uses the shorter
   * "days" wording so chips stay compact.
   */
  private formatTransitChipLabel(min: number, max: number): string {
    const range = (min === max) ? `${min}` : `${min}\u2013${max}`;
    // Reuse the existing `day` / `days` translations that are
    // already in `messages.en.xtb` (both registered pre-2026).
    const noun = max === 1
      ? LanguagePipe.get('day')
      : LanguagePipe.get('days');
    return `${range} ${noun}`;
  }
}

